import React, { MouseEvent, MouseEventHandler } from "react";

import { TextProvider } from "../../../context/Text";
import { TypographyProvider, useTypography } from "../../../context/Typography";
import { useGetLinkComponentAndProps } from "../../../hooks/link";
import { useContentColor } from "../../../styles/colors/content/useContentColor";
import { ContentEmphasis, KeyColorRole } from "../../../styles/colors/types";
import {
  typographyBodyLineHeightMap,
  TypographyBodySize,
  typographyBodySizeMap,
  TypographyLineHeight
} from "../../../styles/typography/typography";
import getLinkStyles from "../utils/getLinkStyles";

import type { LocationShape } from "../../../types";
type Props = {
  children: React.ReactNode | string;
  to: string | LocationShape;
  newTab?: boolean;
  onClick?: MouseEventHandler;
  disabled?: boolean;
  size?: TypographyBodySize;
  lineHeight?: TypographyLineHeight;
  navLink?: boolean;
  exact?: boolean;
  replace?: boolean;
  tone?: KeyColorRole;
  emphasis?: ContentEmphasis;
  onHighEmphasisSurface?: boolean;
};

export default function Link({
  children,
  to,
  onClick,
  newTab,
  disabled = false,
  size: sizeProp,
  lineHeight: lineHeightProp,
  navLink,
  exact,
  replace,
  tone = "primary",
  emphasis = "high",
  onHighEmphasisSurface = false
}: Props) {
  const color = useContentColor({ tone, emphasis, onHighEmphasisSurface });

  const styleProps = getLinkStyles(disabled);

  const { Component, linkProps } = useGetLinkComponentAndProps({
    to,
    disabled,
    newTab,
    navLink,
    styleProps,
    replace
  });

  const {
    typographyBodySize: typographyBodySizeFromContext,
    lineHeight: lineHeightFromContext
  } = useTypography();

  const size = sizeProp || typographyBodySizeFromContext || "medium";
  const lineHeight = lineHeightProp || lineHeightFromContext || "small";

  return (
    <Component
      target={newTab ? "_blank" : undefined}
      onClick={(e: MouseEvent) => {
        !disabled && onClick && onClick(e);
      }}
      {...typographyBodySizeMap[size]}
      {...typographyBodyLineHeightMap[lineHeight]}
      exact={exact}
      fontWeight="bold"
      {...linkProps}
      {...styleProps}
      color={color}
    >
      <TextProvider value>
        <TypographyProvider value={{ typographyBodySize: size }}>
          {children}
        </TypographyProvider>
      </TextProvider>
    </Component>
  );
}
